import React, { useState, useEffect, useMemo } from 'react'
import { Layout } from '../../components/Layout/Layout'
import { Modal } from '../../components/Modal/Modal'
import { CompanyForm } from './CompanyForm'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

// Hash a string to a deterministic brand color (so each company has a stable look)
function colorFromString(s) {
  const palette = ['#a32638', '#ec0016', '#e60005', '#e2231a', '#1a3c8c', '#003082', '#5d2e8c', '#0f6b4f', '#b56014']
  let h = 0
  for (let i = 0; i < (s || '').length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return palette[Math.abs(h) % palette.length]
}

export function Companies() {
  const { t } = useContent()
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api.get('/api/admin/companies?size=100')
      const data = Array.isArray(r.data) ? r.data : r.data.content || []
      setCompanies(data)
    } catch (err) {
      setError(err.response?.data?.message || t('companies.error.load'))
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (company = null) => {
    setSelectedCompany(company)
    setIsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedCompany(null)
  }
  const handleSubmit = async (formData) => {
    setIsSubmitting(true)
    try {
      if (selectedCompany?.id) {
        await api.put(`/api/admin/companies/${selectedCompany.id}`, formData)
      } else {
        await api.post('/api/admin/companies', formData)
      }
      await fetchCompanies()
      handleCloseModal()
    } catch (err) {
      setError(err.response?.data?.message || t('companies.error.save'))
    } finally {
      setIsSubmitting(false)
    }
  }
  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!confirm(t('companies.confirmDelete'))) return
    try {
      await api.delete(`/api/admin/companies/${id}`)
      await fetchCompanies()
    } catch (err) {
      setError(err.response?.data?.message || t('companies.error.delete'))
    }
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return companies
    const s = search.toLowerCase()
    return companies.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(s) ||
        (c.rics || '').toLowerCase().includes(s) ||
        (c.country || '').toLowerCase().includes(s)
    )
  }, [companies, search])

  return (
    <Layout crumb={t('companies.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="companies.title" /></h1>
              <div className="page-head__sub">
                <T k="companies.subtitle" vars={{ count: companies.length }} />
              </div>
            </div>
            <div className="page-head__actions">
              <button className="btn" onClick={() => setSearch('')} title={t('companies.filter.resetTitle')}>
                {Icon.filter({ s: 12 })} {t('companies.filter.country')} {Icon.chevronDown({ s: 10 })}
              </button>
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    filtered,
                    [
                      { key: 'id', label: t('companies.csv.id') },
                      { key: 'name', label: t('companies.csv.name') },
                      { key: 'rics', label: t('companies.csv.rics') },
                      { key: 'country', label: t('companies.csv.country') },
                      { key: 'companyType', label: t('companies.csv.type') },
                      { key: 'status', label: t('companies.csv.status') },
                    ],
                    'mas-companies'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('companies.action.export')}
              </button>
              <button className="btn btn--primary" onClick={() => handleOpenModal()}>
                {Icon.upload({ s: 12 })} {t('companies.action.onboard')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div
          className="filter-bar"
          style={{
            borderRadius: 'var(--r-lg)',
            border: '1px solid var(--border-soft)',
            background: 'var(--surface-0)',
            marginBottom: 14,
          }}
        >
          <div className="search-input">
            {Icon.search({ s: 12 })}
            <input
              placeholder={t('companies.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            <T k="companies.countOf" vars={{ shown: filtered.length, total: companies.length }} />
          </span>
        </div>

        {loading ? (
          <div className="ngrs-skeleton" style={{ height: 200, borderRadius: 'var(--r-lg)' }} />
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>
            {companies.length === 0
              ? <T k="companies.empty.none" />
              : <T k="companies.empty.search" />}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {filtered.map((c) => {
              const code = (c.name || '').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase()
              return (
                <div
                  key={c.id}
                  className="company-card"
                  onClick={() => handleOpenModal(c)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="company-card__logo" style={{ background: colorFromString(c.rics || c.name), color: '#fff' }}>
                    {code || 'NG'}
                  </div>
                  <div>
                    <div className="company-card__name">{c.name}</div>
                    <div className="company-card__meta">
                      <span className="rics" style={{ marginRight: 6 }}>
                        {c.rics || '—'}
                      </span>
                      {c.country || '—'} · {c.companyId?.split(':').pop() || ''}
                    </div>
                  </div>
                  <div className="company-card__stats" style={{ alignItems: 'flex-end' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{c.description || ''}</span>
                    <button
                      className="btn btn--ghost btn--sm"
                      style={{ marginTop: 4 }}
                      onClick={(e) => handleDelete(e, c.id)}
                    >
                      {Icon.close({ s: 11 })} {t('companies.action.remove')}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        </div>

        <Modal
          isOpen={isModalOpen}
          title={selectedCompany ? t('companies.modal.edit') : t('companies.modal.create')}
          onClose={handleCloseModal}
          size="lg"
        >
          <CompanyForm company={selectedCompany} onSubmit={handleSubmit} isLoading={isSubmitting} />
        </Modal>
      </div>
    </Layout>
  )
}
